import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

import { format, sub } from "date-fns";
import type { RadioChangeEvent } from "antd";
import { Descriptions, Space, Card, Tabs, Input, Button, message, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DownloadOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import Map, { Marker } from "../../components/Map";
import {
   DeviceData, MetricsRequest, MetricResponse
} from "../../model/model";

import SessionStore from "../../model/SessionStore";
import MetricChart from "../../components/MetricChart";
import MetricsAggregations from "../../components/MetricsAggregations";
import DeviceLogs from "./DeviceLogs";

interface IProps {
  deviceData: DeviceData;
  lastSeenAt?: Date;
}

function DeviceDashboard(props: IProps) {
  const [metricsAggregator, setMetricsAggregator] = useState<string>('AVG');
  const [metricsDownsampling, setMetricsDownsampling] = useState<string>('1m');
  const [metricsLimit, setMetricsLimit] = useState<number>(1000);
  const [deviceMetrics, setDeviceMetrics] = useState<MetricResponse | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date>(sub(new Date(), { hours: 6 }));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [timeInterval, setTimeInterval] = useState<string>('6h');
  const [port, setPort] = useState<number>(0);
  const [hex, setHex] = useState<string>('');
  const [showSensorTable, setShowSensorTable] = useState<boolean>(false);

  const loadDeviceMetrics = useCallback(
    (start: Date, end: Date, agg: string, ds: string) => {
    
      const req :MetricsRequest = {
       topic: props.deviceData.topic
      ,start: start.getTime()
      ,end: end.getTime()
      ,cols: []
      ,limit: metricsLimit
      ,downsampling: ds
      ,aggregator: agg
      ,desc: false
      };

      SessionStore.getClient().getMetrics(req, SessionStore.getBearer()).then((resp) => {
        setDeviceMetrics(resp.data);
      });
    },
    [props.deviceData],
  );


  const loadMetrics = useCallback(() => {
    loadDeviceMetrics(startDate, endDate, metricsAggregator, metricsDownsampling);
  }, [ loadDeviceMetrics, startDate, endDate, metricsAggregator, metricsDownsampling]);

  useEffect(() => {
    loadMetrics();
  }, [props, metricsAggregator, metricsDownsampling, startDate, endDate, loadMetrics]);

  const onMetricsAggregationChange = (e: RadioChangeEvent) => {
    setMetricsAggregator(e.target.value);
  };

  const onMetricsDownsamplingChange = (e: RadioChangeEvent) => {
    setMetricsDownsampling(e.target.value);
  };

  const handleSendResponse = () => {
    if (!port || !hex) {
      message.warning('Please enter both port and hex message');
      return;
    }
    SessionStore.getClient().postResponse(props.deviceData.id, port, hex, SessionStore.getBearer()).then(() => {
      message.success('Response sent successfully');
    }).catch((err) => {
      message.error('Failed to send response');
    });
  };

  const downloadMetricsCsv = useCallback(async () => {
    if (!props.deviceData.id || !startDate || !endDate) {
      return;
    }
    const token = SessionStore.getToken();
    const response = await axios.post("/api/v1/metrics/csv", {
      ds: props.deviceData.id,
      from: startDate.getTime(),
      to: endDate.getTime(),
      lim: metricsLimit || 10000,
      lastRecs: false,
      col: "",
      dateFormat: "yyyy-MM-dd HH:mm:ss"
    }, {
      headers: { "Authorization": `Bearer ${token}` },
      responseType: "blob"
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `metrics_${props.deviceData.id}_${startDate.getTime()}_${endDate.getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [props.deviceData.id, startDate, endDate, metricsLimit]);


  if (deviceMetrics === undefined) {
    return null;
  }

  let lastSeenAt = "Never";
  if (props.lastSeenAt !== undefined) {
    lastSeenAt = format(props.lastSeenAt, "yyyy-MM-dd HH:mm:ss");
  }

  const loading = !deviceMetrics;

  const buildSensorTableData = (): any[] => {
    if (!deviceMetrics || !deviceMetrics.timestamps || !deviceMetrics.columnNames) return [];
    const rows: any[] = [];
    for (let i = 0; i < deviceMetrics.timestamps.length; i++) {
      const row: any = {
        time: new Date(deviceMetrics.timestamps[i]).toLocaleString()
      };
      for (let j = 0; j < deviceMetrics.columnNames.length; j++) {
        row[deviceMetrics.columnNames[j]] = deviceMetrics.values[j]?.[i] ?? null;
      }
      rows.push(row);
    }
    return rows;
  };

  const buildSensorTableColumns = (): ColumnsType<any> => {
    const cols: ColumnsType<any> = [
      { title: "Time", dataIndex: "time", key: "time", fixed: "left" as const, width: 180 }
    ];
    if (deviceMetrics?.columnNames) {
      for (const name of deviceMetrics.columnNames) {
        cols.push({
          title: name,
          dataIndex: name,
          key: name,
          width: 100,
          render: (val: number | null) => val != null ? val.toFixed(2) : "--"
        });
      }
    }
    return cols;
  };

  const aggregations = (
    <MetricsAggregations
      loading={loading}
      startDate={startDate}
      endDate={endDate}
      setStartDate={setStartDate}
      setEndDate={setEndDate}
      timeInterval={timeInterval}
      setTimeInterval={setTimeInterval}
      metricsAggregator={metricsAggregator}
      setMetricsAggregator={setMetricsAggregator}
      metricsDownsampling={metricsDownsampling}
      setMetricsDownsampling={setMetricsDownsampling}
      loadMetrics={loadMetrics}
      onMetricsAggregationChange={onMetricsAggregationChange}
      onMetricsDownsamplingChange={onMetricsDownsamplingChange}
    />
  );
  const location: [number, number] = [props.deviceData.locLat, props.deviceData.locLong];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Card>
        <Descriptions>
          <Descriptions.Item label="Last seen">{lastSeenAt}</Descriptions.Item>
          <Descriptions.Item label="Device profile">
            <Link
              to={`/device-profiles/${props.deviceData.devProfileId}/edit`}
            >
              {props.deviceData.devProfileId}
            </Link>
          </Descriptions.Item>
          <Descriptions.Item label="Description">{props.deviceData.descr}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Tabs tabBarExtraContent={aggregations}>

        <Tabs.TabPane tab="Device metrics" key="1">
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Card
              title={
                <Space>
                  Sensor data
                  <Button
                    type="text"
                    icon={showSensorTable ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    onClick={() => setShowSensorTable(!showSensorTable)}
                  />
                </Space>
              }
              className="dashboard-chart"
              extra={
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={downloadMetricsCsv}
                >
                  Download CSV
                </Button>
              }
            >
                 {showSensorTable && (
                  <Table
                    columns={buildSensorTableColumns()}
                    dataSource={buildSensorTableData()}
                    rowKey="time"
                    size="small"
                    scroll={{ x: true, y: 400 }}
                    pagination={{ pageSize: 50 }}
                  />
                )}
              <MetricChart metric={deviceMetrics} aggregation={metricsDownsampling} zeroToNull deviceId={props.deviceData.id} startDate={startDate} endDate={endDate} limit={metricsLimit} />
            </Card>
          </Space>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Device logs" key="3">
          <DeviceLogs deviceId={props.deviceData.id} metricsLimit={metricsLimit} startDate={startDate} endDate={endDate} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Map" key="2">
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Map height={500} center={location}>
              <Marker position={location} faIcon="wifi" color="blue" />
            </Map>
          </Space>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Send Response" key="4">
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Card title="Send Hex Response">
              <Space direction="vertical">
                <Input
                  type="number"
                  placeholder="Port"
                  value={port}
                  onChange={(e) => setPort(Number(e.target.value))}
                />
                <Input
                  placeholder="Hex Message"
                  value={hex}
                  onChange={(e) => setHex(e.target.value)}
                />
                <Button type="primary" onClick={handleSendResponse}>
                  Send Response
                </Button>
              </Space>
            </Card>
          </Space>
        </Tabs.TabPane>
      </Tabs>
    </Space>
  );
}

export default DeviceDashboard;
